import { useEffect, useMemo, useRef, useState } from "react";

import { useMachineryBySlug, useSaveMachinery, useLookupsMachinery } from "./useMachinery.js";
import { toast } from "react-toastify";

export function useMachineryForm({ option, filters, elementId, baseObject, onClose }) {
	const isOpen = option === "crear" || option === "editar";
	const isEditMode = option === "editar";
	const modalTitle = useMemo(() => (isEditMode ? "Editar Maquinaria" : "Crear Maquinaria"), [isEditMode]);

	const { mutate: saveObjective, isLoading: isLoadingSave } = useSaveMachinery();
	const { data: lookups, isLoading: isLoadingLookups } = useLookupsMachinery();

	const canLoadDetail = isEditMode && !!elementId && !!lookups?.estates_parents && !!lookups?.estates;
	const { data: element, isLoading: isLoadingElement, isFetching: isFetchingElement } = useMachineryBySlug(filters, elementId, canLoadDetail);

	const [formState, setFormState] = useState({ ...baseObject });
	const [formErrors, setFormErrors] = useState({});

	const estateParentOptions = useMemo(() => lookups?.estates_parents ?? [], [lookups]);
	const estateOptions = useMemo(() => lookups?.estates ?? [], [lookups]);
	const types = useMemo(() => lookups?.types ?? [], [lookups]);

	const estatesByParentMap = useMemo(() => {
		const map = new Map();
		for (const estate of estateOptions) {
			if (!map.has(estate.estate_parent_id)) map.set(estate.estate_parent_id, []);
			map.get(estate.estate_parent_id).push(estate);
		}
		return map;
	}, [estateOptions]);

	const initAutoSelectDoneRef = useRef(false);

	useEffect(() => {
		if (!isOpen || !lookups || isLoadingLookups || initAutoSelectDoneRef.current) return;
		setFormState((previous) => {
			const next = { ...previous };

			const hasNoParents = !Array.isArray(next.estate_parent_id) || next.estate_parent_id.length === 0;
			if (hasNoParents && estateParentOptions.length === 1) {
				const onlyParentId = estateParentOptions[0].value;
				next.estate_parent_id = [onlyParentId];

				const estatesForOnlyParent = estatesByParentMap.get(onlyParentId) || [];
				const hasNoEstates = !Array.isArray(next.estate_id) || next.estate_id.length === 0;
				if (hasNoEstates && estatesForOnlyParent.length === 1) {
					next.estate_id = [estatesForOnlyParent[0].value];
				}
			}

			return next;
		});
		initAutoSelectDoneRef.current = true;
	}, [isOpen, lookups, isLoadingLookups, estateParentOptions, estatesByParentMap, types]);

	const handleTypeChange = (selectedTypeId) => {
		setFormState((previous) => ({
			...previous,
			machinery_type_id: selectedTypeId,
		}));

		setFormErrors((previousErrors) => {
			const nextErrors = { ...previousErrors };
			delete nextErrors.machinery_type_id;
			return nextErrors;
		});
	};

	const handleEstateParentsChange = (selectedParentIds) => {
		const nextSelectedParentIds = selectedParentIds || [];

		const validEstateIdSet = new Set(nextSelectedParentIds.flatMap((parentId) => (estatesByParentMap.get(parentId) || []).map((estate) => estate.value)));

		const previousSelectedEstateIds = Array.isArray(formState.estate_id) ? formState.estate_id : [];
		const prunedSelectedEstateIds = previousSelectedEstateIds.filter((id) => validEstateIdSet.has(id));

		let nextSelectedEstateIds = prunedSelectedEstateIds;
		if (prunedSelectedEstateIds.length === 0) {
			const allValidEstateIds = Array.from(validEstateIdSet);
			nextSelectedEstateIds = allValidEstateIds.length === 1 ? [allValidEstateIds[0]] : [];
		}

		setFormState((previous) => ({
			...previous,
			estate_parent_id: nextSelectedParentIds,
			estate_id: nextSelectedEstateIds,
		}));

		setFormErrors((previousErrors) => {
			const nextErrors = { ...previousErrors };
			delete nextErrors.estate_parent_id;
			delete nextErrors.estate_id;
			return nextErrors;
		});
	};

	const filteredEstateOptions = useMemo(() => {
		const selectedParentIds = formState.estate_parent_id || [];
		if (!selectedParentIds.length) return [];
		const seen = new Set();
		const merged = [];
		for (const parentId of selectedParentIds) {
			for (const estate of estatesByParentMap.get(parentId) || []) {
				if (!seen.has(estate.value)) {
					seen.add(estate.value);
					merged.push(estate);
				}
			}
		}
		return merged;
	}, [formState.estate_parent_id, estatesByParentMap]);

	useEffect(() => {
		if (isEditMode && element) {
			const uniqueEstatesParents = Array.from(new Set((element.estates_parents || []).map((v) => Number(v.value))));
			const uniqueEstates = Array.from(new Set((element.estates || []).map((v) => Number(v.value))));

			setFormState((prev) => ({
				...prev,
				...element,
				estate_parent_id: uniqueEstatesParents ?? [],
				estate_id: uniqueEstates ?? [],
				machinery_type_id: element.type?.value || "",
				id: element.id,
			}));
		}
	}, [isEditMode, element]);

	const validateForm = (state) => {
		const next = {};
		if (!Array.isArray(state.estate_parent_id) || state.estate_parent_id.length === 0) next.estate_parent_id = "Requerido";
		if (!Array.isArray(state.estate_id) || state.estate_id.length === 0) next.estate_id = "Requerido";
		if (!state.name?.trim()) next.name = "Requerido";
		if (!state.machinery_type_id) next.machinery_type_id = "Requerido";
		return next;
	};

	const buildPayload = (state) => {
		const uniqueParents = Array.from(new Set((state.estate_parent_id || []).map(Number)));
		const uniqueEstates = Array.from(new Set((state.estate_id || []).map(Number)));

		return {
			id: isEditMode ? Number(state.id) : undefined,
			filters: filters,
			form: {
				estate_parent_id: uniqueParents,
				estate_id: uniqueEstates,
				name: state.name?.trim() || "",
				machinery_type_id: state.machinery_type_id ? Number(state.machinery_type_id) : undefined,
			},
		};
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		const nextErrors = validateForm(formState);
		setFormErrors(nextErrors);
		if (Object.keys(nextErrors).length) return;

		const payload = buildPayload(formState);

		saveObjective(payload, {
			onSuccess: () => {
				toast.success(isEditMode ? "Maquinaria actualizada" : "Maquinaria creada");
				onClose();
			},
			onError: (error) => {
				toast.error(<div style={{ whiteSpace: "pre-line" }}>{error?.response?.data?.message || "Error al guardar el Maquinaria"}</div>);
			},
		});
	};

	return {
		isOpen,
		isEditMode,
		modalTitle,
		isLoadingSave,
		isLoadingElement,
		isFetchingElement,

		estateParentOptions,
		filteredEstateOptions,
		types,

		formState,
		setFormState,
		formErrors,
		setFormErrors,

		handleEstateParentsChange,
		handleTypeChange,
		handleSubmit,
	};
}
